import { ErrorMessages } from "@outschool/errors";
import { useTranslation } from "@outschool/localization";
import { createActivitySlug, createPathWithActivity } from "@outschool/routes";
import { getIdFromSlug } from "@outschool/routes/src";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  ClassPageVisitHistory,
  ListingCardFragment,
  SimilarListingsMiniTiles
} from "@outschool/ui-components-website";
import { Loading } from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";

import * as Routes from "../../shared/Routes";
import NotEnrolledView from "../components/detailPage/NotEnrolledView";
import SimpleLayout from "../components/layouts/SimpleLayout";
import {
  ActivityDetailPageProvider,
  useActivityDetailPageContext
} from "../hooks/ActivityDetailPage";
import { SearchFiltersContextProvider } from "../lib/useSearchFilters";
import NotFoundPage from "./NotFoundPage";

const similarListingsQuery = gql`
  query SimilarListingsUnpublished(
    $activitySlugId: String!
    $withRecentReviews: Boolean!
    $skipRating: Boolean!
  ) {
    similarListingsUnpublished(activitySlugId: $activitySlugId) {
      popularElasticsearch {
        ...ListingCard_listing
      }
    }
  }
  ${ListingCardFragment}
`;

function ActivityDetailPage() {
  const { isLoggedIn } = useSession();
  const location = useLocation();
  const history = useHistory();
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const { t } = useTranslation("client\\routes\\ActivityDetailPage");

  const {
    activity,
    error,
    loading: activityIsLoading
  } = useActivityDetailPageContext();

  const unpublished =
    error && _.endsWith(error.message, ErrorMessages.CLASS_NO_LONGER_AVAILABLE);
  const notFound = !activityIsLoading && !activity;
  const showDetails = !activityIsLoading && !!activity;
  const allProps = {
    loading: activityIsLoading,
    notFound,
    unpublished,
    showDetails
  };

  React.useEffect(() => {
    if (!activity) {
      return;
    }

    if (!hasLoaded) {
      ClassPageVisitHistory.pushToTop(activity.uid);
      setHasLoaded(true);
    }
  }, [activity, hasLoaded, location.query.sectionUid]);

  const activitySlug = location?.pathname?.split("/")[2];
  const activitySlugId = getIdFromSlug(activitySlug);
  const { data: similarListingsData, loading: similarListingsLoading } =
    useQueryWithPreviousData(similarListingsQuery, {
      skip: !unpublished,
      variables: {
        activitySlugId,
        skipRating: false,
        withRecentReviews: false
      }
    });

  // redirect /classes/ to /groups/
  React.useEffect(() => {
    if (activityIsLoading || !activity) {
      return;
    }
    if (
      (activity.isClub && history.location.pathname.startsWith("/classes/")) ||
      (!activity.isClub && history.location.pathname.startsWith("/groups/"))
    ) {
      history.replace(Routes.activityPath(activity));
    }

    const canonicalActivitySlug = createActivitySlug(
      activity.title,
      activity.slug_id
    );

    if (!isLoggedIn && activitySlug !== canonicalActivitySlug) {
      const canonicalActivityPath = createPathWithActivity(
        activity,
        null,
        location.query
      );
      history.replace(canonicalActivityPath);
    }
  }, [
    activityIsLoading,
    activity,
    history,
    location,
    isLoggedIn,
    activitySlug
  ]);

  if (activityIsLoading) {
    return (
      <SimpleLayout {...allProps} allowCategoriesNavbar>
        <Loading delay={0} />
      </SimpleLayout>
    );
  }
  // we don't generate CDPs for unpublished classes in SSR (SSR returns a 404)
  // so we handle that case here by recommending similar listings
  else if (unpublished) {
    const similarListings =
      similarListingsData?.similarListingsUnpublished?.popularElasticsearch;
    if (similarListings?.length) {
      return (
        <SimpleLayout {...allProps} allowCategoriesNavbar>
          <SimilarListingsMiniTiles
            activity={{ isPublished: false, uid: "" }}
            listings={similarListings}
            isFullPage
          />
        </SimpleLayout>
      );
    } else if (similarListingsLoading) {
      return (
        <SimpleLayout {...allProps} allowCategoriesNavbar>
          <Loading delay={0} />
        </SimpleLayout>
      );
    } else {
      return <NotFoundPage message={t("This class is no longer available")} />;
    }
  } else if (notFound) {
    return <NotFoundPage />;
  }
  return (
    <NotEnrolledView
      // @ts-ignore TS(2322): Type '{ __typename?: "Activity" | undefined; upsel... Remove this comment to see the full error message
      activity={activity}
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      leader={activity.leader}
      showDetails={showDetails}
    />
  );
}

export default function ActivityDetailPageWrapped() {
  return (
    <ActivityDetailPageProvider>
      <SearchFiltersContextProvider>
        <ActivityDetailPage />
      </SearchFiltersContextProvider>
    </ActivityDetailPageProvider>
  );
}
